import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Dialog,
  Divider,
  LinearProgress,
  List,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  ListItemText,
  Paper,
  Typography,
} from '@material-ui/core';
import React, { useCallback, useEffect, useState } from 'react';
import { useFirestore, useFirestoreConnect } from 'react-redux-firebase';
import { LoginBox } from '../components/organisms';
import AdminMenu from '../components/organisms/AdminPharmaMenu';
import PageLayout from '../components/organisms/PageLayout';
import { useAuthentication } from '../hooks';
import { useSelector } from 'react-redux';
import { Profile, RootState } from '../store/reducers';

const Register: React.FC = () => {
  const [openRegister, setOpenRegister] = useState(false);
  const users = useSelector((state: RootState) => state.firestore.ordered.users_meta);
  const toggleRegister = useCallback(() => setOpenRegister(!openRegister), [openRegister]);
  const [loading, setLoading] = useState(false);
  const [parent, setParent] = useState<Profile | null>(null);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [searchText, setSearchText] = useState('');
  const firestore = useFirestore();

  const {
    profile: { isAdmin },
  } = useAuthentication();

  const handleDeleteUsers = useCallback(async () => {
    setLoading(true);
    for (let userId of selectedUsers) {
      const children = users.filter((user) => user.parent === userId);
      for (let child of children) {
        await firestore.collection('users_meta').doc(child.id).update({ inactive: true });
      }
      await firestore.collection('users_meta').doc(userId).update({ inactive: true });
    }
    window.location.reload();
    setLoading(false);
  }, [firestore, selectedUsers, users]);

  const handleRegisterChild = useCallback(
    (parent) => {
      toggleRegister();
      setParent(parent);
    },
    [toggleRegister]
  );

  useEffect(() => {
    !openRegister && setParent(null);
  }, [openRegister]);

  useFirestoreConnect({ collection: 'users_meta' });

  const sortAndFilter = useCallback(
    (users = []): Profile[] => {
      return users
        .filter(
          (user: Profile) =>
            !user.inactive && (!searchText || user?.email?.toLowerCase()?.indexOf(searchText.toLowerCase()) > -1)
        )
        .sort(function (a: Profile, b: Profile) {
          if (a.email < b.email) {
            return -1;
          }
          if (a.email > b.email) {
            return 1;
          }
          return 0;
        });
    },
    [searchText]
  );

  const toggleSelectAll = useCallback(() => {
    if (selectedUsers.length === sortAndFilter(users).length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(sortAndFilter(users).map((user) => user.id));
    }
  }, [selectedUsers.length, sortAndFilter, users]);

  return (
    <PageLayout>
      {isAdmin && (
        <AdminMenu
          onSearch={setSearchText}
          onAddUser={toggleRegister}
          onDeleteUsers={!!selectedUsers.length ? handleDeleteUsers : undefined}
        />
      )}
      {loading && <LinearProgress />}
      {openRegister && (
        <Dialog open onClose={toggleRegister}>
          <Box padding={2} paddingBottom={0}>
            <Typography variant="h5">Registra nuova utenza</Typography>
          </Box>
          <Box paddingY={2} width="500px">
            <LoginBox showRegister parent={parent} onRegisterCallback={toggleRegister} users={users ?? []} />
          </Box>
        </Dialog>
      )}
      <Box display="flex" justifyContent="end" p={2} paddingBottom={0} alignItems="center">
        Seleziona tutti
        <Checkbox
          color="primary"
          checked={selectedUsers.length === sortAndFilter(users).length}
          onChange={toggleSelectAll}
        />
      </Box>
      <List>
        {sortAndFilter(users)
          .filter((u) => !u.parent)
          .map((user) => (
            <div key={user.id}>
              <ListItem>
                <ListItemAvatar>
                  <Avatar variant="square" style={{ width: 50, height: 50, marginRight: 20 }} />
                </ListItemAvatar>
                <ListItemText primary={user.email} secondary={user.name ?? user.email} />
                <ListItemSecondaryAction>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    disableElevation
                    onClick={() => handleRegisterChild(user)}
                  >
                    Registra account associato
                  </Button>
                  <Checkbox
                    color="primary"
                    checked={selectedUsers.includes(user.id)}
                    onChange={(_e, checked) => {
                      if (checked) setSelectedUsers([...selectedUsers, user.id]);
                      else setSelectedUsers(selectedUsers.filter((id) => id !== user.id));
                    }}
                  />
                </ListItemSecondaryAction>
              </ListItem>
              {sortAndFilter(users).some(({ parent }) => parent && parent === user.id) && (
                <Paper variant="outlined" style={{ marginLeft: 70, marginBottom: 10 }}>
                  <List dense>
                    {sortAndFilter(users)
                      .filter(({ parent }) => parent && parent === user.id)
                      .map((child) => (
                        <ListItem dense key={child.id}>
                          <ListItemAvatar>
                            <Avatar variant="square" style={{ width: 35, height: 35, marginRight: 10 }} />
                          </ListItemAvatar>
                          <ListItemText primary={child.email} secondary={child.name ?? child.email} />
                          <ListItemSecondaryAction>
                            <Checkbox
                              color="primary"
                              checked={selectedUsers.includes(user.id) || selectedUsers.includes(child.id)}
                              onChange={(_e, checked) => {
                                if (checked) setSelectedUsers([...selectedUsers, child.id]);
                                else setSelectedUsers(selectedUsers.filter((id) => id !== child.id));
                              }}
                            />
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                  </List>
                </Paper>
              )}
              <Divider variant="inset" component="li" />
            </div>
          ))}
      </List>
    </PageLayout>
  );
};

export default Register;
